import * as React from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { Keyframes, animated } from 'react-spring/renderprops.cjs';
import { useTheme } from 'styled-components';
import ChevronDown from '@mybet/components/Icons/ChevronDown';
import { Locale } from '@mybet/core/libs/types';
import Flex from '@mybet/components/Flex';

// TODO: Setup animation components or spring configs for reuse across app
// will need some thought before implementation so hold off until post launch
export const ListSpring: any = Keyframes.Spring({
  open: {
    delay: 0,
    height: 'auto',
    config: { tension: 220, friction: 25, clamp: true },
  },
  close: {
    delay: 0,
    height: 0,
    config: { tension: 220, friction: 25, clamp: true },
  },
});

export const ArrowSpring: any = Keyframes.Spring({
  up: {
    transform: 'rotate(-180deg)',
    transformOrigin: 'center',
    width: 16,
    height: 16,
  },
  down: {
    transform: 'rotate(0deg)',
    transformOrigin: 'center',
    width: 16,
    height: 16,
  },
});

type LanguageConfig = {
  lang: string;
  flag: string;
};

type LanguageOptions = {
  en: LanguageConfig;
  de: LanguageConfig;
};

export type LanguageSelectorProps = {
  onToggle?(lang: Locale): void;
  lock?: boolean;
  locale?: Locale;
};

const options: LanguageOptions = {
  en: { lang: 'English', flag: 'united_kingdom_square' },
  de: { lang: 'Deutsch', flag: 'germany_square' },
};

const LanguageWrapper = styled.div<{ editMode: boolean }>`
  background-color: ${({ theme, editMode }): string =>
    editMode ? theme.white : theme.grey01};
  border: 1px solid
    ${({ editMode }): string => (editMode ? '#CED2D7' : 'transparent')};
  border-radius: 2px;
  width: 100%;
  user-select: none;
  overflow: hidden;
  margin-top: 8px;
  margin-bottom: 14px;
  color: ${({ theme }): string => theme.blue13};
`;

const FlagWrapper = styled(Flex)((props) => ({
  pointerEvents: 'none',
  marginRight: props.theme.spacing(),
}));

const SelectedLanguage = styled.div<{
  editMode: boolean;
  lock: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
  width: 100%;
  height: 40px;
  border-bottom: 1px solid
    ${({ editMode }): string => (editMode ? '#E9ECF1' : 'transparent')};
  font-size: 14px;
  line-height: 16px;
  cursor: ${({ lock }): string => (lock ? 'default' : 'pointer')};

  p {
    position: relative;
    margin-right: auto;
    pointer-events: none;
  }

  & img,
  & svg,
  & div {
    pointer-events: none;
  }
`;

const LangSelect = styled(animated.ul)<any>`
  list-style: none;
  padding: 0;
  width: 100%;
  height: 40px;
  -webkit-appearance: none;
  border: none;
  font-size: 14px;
  overflow: hidden;
  margin-top: 0;
  margin-bottom: 0;
  padding: 0 12px;
`;

const LanguageOption = styled(animated.li)((props) => ({
  display: 'flex',
  alignItems: 'center',
  height: 40,
  width: '100%',
  cursor: 'pointer',
  userSelect: 'none',
  '&:active': {
    backgroundColor: props.theme.grey01,
  },
  '& > p': {
    margin: 0,
    height: 40,
    display: 'flex',
    alignItems: 'center',
  },

  [props.theme.breakpoints.up('sm')]: {
    '&:hover': {
      backgroundColor: props.theme.menuLink,
    },
  },
}));

export const LanguageSelector: React.FC<LanguageSelectorProps> = (props) => {
  const { onToggle, lock = false, locale = 'de' } = props;
  const theme = useTheme();
  const selectList = React.useRef(null);
  const selectItem = React.useRef(null);
  const [isEditMode, setEditMode] = React.useState(false);

  const clickEvent = (e: any): void => {
    if (selectList.current !== e.target) {
      setEditMode(false);
    }
  };

  const handleToggle = async (value: Locale) => {
    setEditMode(false);
    onToggle?.(value);
  };

  const handleSelectClick = (): void => {
    if (!lock) {
      setEditMode((prevEditMode) => !prevEditMode);
    }
  };

  React.useEffect(() => {
    window.addEventListener('click', clickEvent);
    return () => {
      window.removeEventListener('click', clickEvent);
    };
  }, []);

  return (
    <LanguageWrapper editMode={isEditMode}>
      <SelectedLanguage
        lock={lock}
        onClick={handleSelectClick}
        editMode={isEditMode}
        ref={selectList}
      >
        <FlagWrapper alignItems="center">
          <Image
            src={`/flags/${options[locale].flag}.svg`}
            height="16px"
            width="22px"
          />
        </FlagWrapper>
        <p>{options[locale].lang}</p>
        {!lock && (
          <ArrowSpring native state={isEditMode ? 'up' : 'down'}>
            {(styles: any): React.ReactElement => (
              <animated.div style={styles}>
                <ChevronDown
                  strokeWidth="2"
                  fill={theme.grey08}
                  height={16}
                  width={16}
                />
              </animated.div>
            )}
          </ArrowSpring>
        )}
      </SelectedLanguage>
      {!lock && (
        <ListSpring native state={isEditMode ? 'open' : 'close'}>
          {(styles: any) => (
            <LangSelect style={styles} value={locale}>
              {['en', 'de'].map(
                (language) =>
                  language !== locale && (
                    <LanguageOption
                      key={language}
                      value={language}
                      onClick={() => handleToggle(language as Locale)}
                      ref={selectItem}
                    >
                      <FlagWrapper>
                        <Image
                          src={`/flags/${options[language as Locale].flag}.svg`}
                          height="16px"
                          width="22px"
                        />
                      </FlagWrapper>
                      <p>{options[language as Locale].lang}</p>
                    </LanguageOption>
                  )
              )}
            </LangSelect>
          )}
        </ListSpring>
      )}
    </LanguageWrapper>
  );
};

export default React.memo(LanguageSelector);
